import { all, call, put, select, takeLeading } from 'typed-redux-saga'
import { errorHandler, NewsNS, newsSlice, newsApi, News, newsSelectors, gridSlice } from 'Modules'
import { difference } from 'lodash'
import { ReceivingFileModel, RequestParams, ResponseCatalog } from 'Models'

function* getAllNews(action: NewsNS.GetAll) {
	try {
		const { params, clear = false, callback } = action.payload
		yield* put(gridSlice.actions.setLoading(true))
		const res = yield* call(newsApi.getAll, params)
		const news = new ResponseCatalog<News>(res)
		if (!clear) {
			const oldNews = yield* select(newsSelectors.getAll)
			news.entries = oldNews.concat(res.entries)
		}
		yield* put(newsSlice.actions.setAll(news))
		yield* put(newsSlice.actions.setRequestParams(params))
		yield* put(newsSlice.actions.getStats())
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* createNews(action: NewsNS.CreateOne) {
	try {
		const { news: addNews, files } = action.payload
		const New = yield* call(newsApi.createOne, addNews)
		if (files && files.length) {
			yield* all(files.map((file): any => call(newsApi.sendNewsAttach, file, New.id)))
		}
		yield* put(newsSlice.actions.getAll({ params: new RequestParams(), clear: true }))
		const grid = document.querySelector('.news__drawer .pp-infinity-scroll')
		grid?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* updateNews(action: NewsNS.UpdateOne) {
	try {
		const { news, files, id } = action.payload
		const stateNews = yield* select(newsSelectors.getAll)
		const stateEditedNews = stateNews.find((entry) => entry.id === news.id)
		const deletedDocuments = difference(
			stateEditedNews?.documents?.map((d: ReceivingFileModel): number => d.id),
			news.documents?.map((d: ReceivingFileModel): number => d.id) || []
		)
		if (deletedDocuments.length) {
			yield* all(
				deletedDocuments.map((doc_id: number): any => call(newsApi.deleteNewsAttach, doc_id))
			)
		}
		yield* call(newsApi.updateOne, news, id)
		if (files && files.length) {
			yield* all(
				files.map((file: any): any => {
					if (!file.id) return call(newsApi.sendNewsAttach, file, news.id)
					return file
				})
			)
		}
		yield* put(newsSlice.actions.setOne(news))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* deleteNews(action: NewsNS.DeleteOne) {
	try {
		yield* call(newsApi.deleteOne, action.payload)
		const stateNews = yield* select(newsSelectors.getAll)
		const stateEditedNews = stateNews.find((entry) => entry.id === action.payload)
		if (stateEditedNews?.documents?.length) {
			yield* all(
				stateEditedNews.documents.map((document: ReceivingFileModel): any =>
					call(newsApi.deleteNewsAttach, document.id)
				)
			)
		}
		yield* put(newsSlice.actions.getAll({ params: new RequestParams(), clear: true }))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* readNews(action: NewsNS.ReadOne) {
	try {
		const res = yield* call(newsApi.readOne, action.payload)
		yield* put(newsSlice.actions.setOne(res))
		yield* put(newsSlice.actions.getStats())
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* deleteNewsAttach(action: NewsNS.DeleteNewsAttach) {
	try {
		const { fileId } = action.payload
		yield* call(newsApi.deleteNewsAttach, fileId)
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getStatsNews() {
	try {
		const res = yield* call(newsApi.getStats)
		yield* put(newsSlice.actions.setStats(res))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

export const newsSaga = function* saga() {
	yield* takeLeading(newsSlice.actions.getAll, getAllNews)
	yield* takeLeading(newsSlice.actions.createOne, createNews)
	yield* takeLeading(newsSlice.actions.updateOne, updateNews)
	yield* takeLeading(newsSlice.actions.deleteOne, deleteNews)
	yield* takeLeading(newsSlice.actions.readOne, readNews)
	yield* takeLeading(newsSlice.actions.deleteNewsAttach, deleteNewsAttach)
	yield* takeLeading(newsSlice.actions.getStats, getStatsNews)
}
